import { ReactNode } from 'react';

import { Endpoints } from '@/api';
import { FilterCheckboxItem } from '@/design_system/Filters/FiltersMenu/FilterCheckboxItem';
import { FilterSearch } from '@/design_system/Filters/FiltersMenu/FilterSearch';
import {
  FilterDivider,
  FilterItemNoResults,
  FiltersMenu,
  FiltersSubMenu,
} from '@/design_system/Filters/FiltersMenu/FiltersMenu';
import { FilterTag } from '@/design_system/Filters/FiltersMenu/FilterTag';

import { useEntityFilterState } from './useEntityFilter';

const MINIMUM_ENTITIES_FOR_SEARCH = 5;

export const DesktopEntityFilter = ({
  selectedEntities,
  setSelectedEntities,
  includeGroups = true,
  traits,
  tag,
  label,
  searchPlaceholder,
  tagSelectedCountLabel,
  icon,
}: {
  selectedEntities: { id: string; name: string }[];
  setSelectedEntities: (entities: { id: string; name: string }[]) => void;
  includeGroups?: boolean;
  traits?: Endpoints['GET /entities-and-groups']['query']['traits'];
  tag?: boolean;
  label: string;
  searchPlaceholder: string;
  tagSelectedCountLabel: string;
  icon: ReactNode;
}) => {
  const { search, setSearch, fetchNextPage, isLoading, entityGroups, entities, count } =
    useEntityFilterState({ traits });

  const selectedEntitiesIds = selectedEntities.map(({ id }) => id);

  const toggleEntity = (entity: { id: string; name: string }, checked: boolean) => {
    if (checked && !selectedEntitiesIds.includes(entity.id)) {
      setSelectedEntities([...selectedEntities, entity]);
    } else if (!checked) {
      setSelectedEntities(selectedEntities.filter(({ id }) => id !== entity.id));
    }
  };

  const options: ReactNode[] = [];

  if (includeGroups && entityGroups.length) {
    options.push(
      ...entityGroups.map((entityGroup) => (
        <FilterCheckboxItem
          key={entityGroup.id}
          checked={selectedEntitiesIds.includes(entityGroup.id)}
          onChange={(checked) => toggleEntity(entityGroup, checked)}
        >
          <span className="text-ellipsis">{entityGroup.name}</span>
        </FilterCheckboxItem>
      ))
    );
  }

  if (includeGroups && entityGroups.length && entities.length) {
    options.push(<FilterDivider sub key="divider" />);
  }

  if (entities.length) {
    options.push(
      ...entities.map((entity) => (
        <FilterCheckboxItem
          key={entity.id}
          checked={selectedEntitiesIds.includes(entity.id)}
          onChange={(checked) => toggleEntity(entity, checked)}
        >
          <span className="text-ellipsis">{entity.name}</span>
        </FilterCheckboxItem>
      ))
    );
  }

  if (options.length === 0) {
    options.push(<FilterItemNoResults key="empty-desktop" />);
  }

  const searchWithOptions = (
    <FilterSearch
      inputProps={{
        ariaLabel: searchPlaceholder,
        placeholder: searchPlaceholder,
        value: search,
        onChange: setSearch,
      }}
      onLoadMore={fetchNextPage}
      isLoading={entityGroups.length === 0 && entities.length === 0 && isLoading}
      isLoadingMore={isLoading}
    >
      {options}
    </FilterSearch>
  );

  const selectedEntitiesCount = selectedEntities.length;

  const children = count < MINIMUM_ENTITIES_FOR_SEARCH ? options : searchWithOptions;

  if (tag) {
    if (!selectedEntitiesCount) {
      return null;
    }

    return (
      <FiltersMenu
        button={
          <FilterTag
            label={label}
            value={selectedEntitiesCount === 1 ? selectedEntities[0].name : tagSelectedCountLabel}
            tooltip={
              selectedEntitiesCount > 1
                ? selectedEntities.map(({ name }) => name).join(', ')
                : undefined
            }
            onClear={() => setSelectedEntities([])}
          />
        }
      >
        {children}
      </FiltersMenu>
    );
  }

  return (
    <FiltersSubMenu icon={icon} label={label}>
      {children}
    </FiltersSubMenu>
  );
};
